import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { BasicButton } from '../../../../../../widgets/buttons';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { showInfoNotification } from '../../../../../../widgets/notificationToaster';
import { ZSwitch } from '../../../../../../widgets/zswitch/zswitch';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import React, { FC, useCallback } from 'react';
import Select from 'react-select';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ascComparator, T } from 'src/shared/helpers';
import GroupIcon from 'src/shared/material_icons/action/join_inner.svg';
import styled from 'styled-components';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import { getRetargetObjectsAndMaterials, retargetMapToActions } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { useTreeNodeManager } from '../../tree';
import {
    ComposerDetailCard,
    ComposerDetailLabel,
    ComposerDetailsStack,
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerResizeHandle,
    ComposerScrollBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectName,
    ComposerSubjectNameInput,
    ComposerTabs,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerPreview from '../composerPreview';
import ComposerActions from './composerActions';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributeType, ComposerOptionActionNode } from './composerAttributesModels';
import ComposerOptions from './composerOptions';

const EMPTY_OPTIONS: ComposerAttributeOptionNode[] = [];
const EMPTY_ACTIONS: ComposerOptionActionNode[] = [];

const WorkspaceGrid = styled.div<{ $optionsWidth?: number; $detailsWidth?: number }>`
    display: grid;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    grid-template-columns:
        ${(props) => (props.$optionsWidth ? `${props.$optionsWidth}px` : 'minmax(0, var(--c2, 2fr))')} 6px
        ${(props) => (props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(0, var(--c3, 3fr))')} 6px minmax(0, var(--c4, 3fr));
    overflow: hidden;
    background: var(--composer-app, #eff3f7);

    > section:nth-child(3) {
        background: var(--composer-sunken, #f4f7fa);
    }

    @media (max-width: 940px) {
        grid-template-columns: 1fr;

        > section:not(:last-child),
        > [role='separator'] {
            display: none;
        }

        > section:last-child,
        > :last-child {
            grid-area: 1 / 1;
        }
    }
`;

const SelectionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SelectionItem = styled.div`
    padding: 9px 10px;
    color: ${(props) => props.theme.app.textColor};
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 13px;
`;

const BulkButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
`;

const SubjectPanel: FC<{ subject?: ComposerAttributeNode; attributes: ComposerAttributeNode[] }> = ({ subject, attributes }) => {
    const [updateAttribute, validateAttributes, sceneObjects] = useComposerStore((store) => [
        store.updateAttribute,
        store.validateAttributes,
        store.sceneObjectList
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);

    if (!subject) {
        return (
            <ComposerPanel>
                <ComposerEmptyState>{T._('Select one group, step or attribute to inspect it.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const isGroup = subject.type === ComposerAttributeType.AttributeGroup;
    const typeLabel = isGroup ? T._('Group', 'Admin') : T._('Step', 'Admin');
    const childAttributes = attributes.filter((attribute) => attribute.parentID === subject.id);
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const objectOptions = sceneObjects.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedObjects = (subject.objects || '')
        .split(/[;,]/)
        .filter(Boolean)
        .map((id) => ({ value: id, label: objectOptions.find((option) => option.value === id)?.label || id }));
    const updateSubject = (changes: Partial<ComposerAttributeNode>) => updateAttribute({ ...subject, ...changes, isNew: true });

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind={isGroup ? 'group' : 'neutral'}>
                    {subject.img ? <img src={subject.img} alt='' /> : <GroupIcon />}
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={isGroup ? T._('Group name', 'Admin') : T._('Step name', 'Admin')}
                        value={subject.name}
                        onChange={(event) => updateSubject({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind={isGroup ? 'group' : 'neutral'}>{typeLabel}</ComposerKindBadge>
                        <span>{T._(`${childAttributes.length} attributes`, 'Admin')}</span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            {/* Neither subject carries a constraint expression the product input can store, so the panel offers Settings alone.
                The shared face is left untouched, so the Links face an attribute was on survives a detour through a group. */}
            <ComposerTabs selectedIndex={0}>
                <TabList>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerScrollBody>
                        <ComposerFormStack>
                            {isGroup && (
                                <>
                                    <FormControl label={T._('Group image URL', 'Admin')}>
                                        <Input
                                            type='url'
                                            value={subject.img || ''}
                                            placeholder={T._('Paste an image URL', 'Admin')}
                                            onChange={(event) => updateSubject({ img: event.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Attributes direction', 'Admin')}>
                                        <Select
                                            options={[
                                                { value: MPlaza.AttributeGroupDirection.Horizontal as number, label: T._('Horizontal', 'Admin') },
                                                { value: MPlaza.AttributeGroupDirection.Vertical as number, label: T._('Vertical', 'Admin') }
                                            ]}
                                            value={
                                                subject.direction === MPlaza.AttributeGroupDirection.Vertical
                                                    ? { value: MPlaza.AttributeGroupDirection.Vertical as number, label: T._('Vertical', 'Admin') }
                                                    : { value: MPlaza.AttributeGroupDirection.Horizontal as number, label: T._('Horizontal', 'Admin') }
                                            }
                                            onChange={(option) => updateSubject({ direction: option?.value ?? MPlaza.AttributeGroupDirection.Horizontal })}
                                            menuPosition='fixed'
                                        />
                                    </FormControl>
                                    {subject.direction === MPlaza.AttributeGroupDirection.Vertical && (
                                        <FormControl label={T._('Keep all attributes open', 'Admin')}>
                                            <ZSwitch
                                                ariaLabel={T._('Keep all attributes open', 'Admin')}
                                                checked={!!subject.attributesAlwaysOpened}
                                                onChange={(checked) => updateSubject({ attributesAlwaysOpened: checked })}
                                            />
                                        </FormControl>
                                    )}
                                    <ComposerUnsupportedNote>
                                        {T._(
                                            'The current group API exposes one image URL but no separate icon field; the prototype icon cannot be stored independently.',
                                            'Admin'
                                        )}
                                    </ComposerUnsupportedNote>
                                </>
                            )}
                            <FormControl label={T._('Camera position', 'Admin')}>
                                <Select
                                    isClearable
                                    options={cameraOptions}
                                    value={cameraOptions.find((option) => option.value === subject.camera) ?? null}
                                    onChange={(option) => updateSubject({ camera: option?.value || '' })}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            {isGroup && (
                                <FormControl
                                    label={T._('Objects', 'Admin')}
                                    tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                                >
                                    <Select
                                        isMulti
                                        options={objectOptions}
                                        value={selectedObjects}
                                        onChange={(options) => updateSubject({ objects: options.map((option) => option.value).join(';') })}
                                        menuPosition='fixed'
                                    />
                                </FormControl>
                            )}
                            <ComposerDetailCard>
                                <ComposerDetailLabel>{T._('Attributes', 'Admin')}</ComposerDetailLabel>
                                <ComposerDetailValue>
                                    {childAttributes.map((attribute) => attribute.name).join(', ') || T._('No attributes assigned', 'Admin')}
                                </ComposerDetailValue>
                            </ComposerDetailCard>
                        </ComposerFormStack>
                    </ComposerScrollBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

const MultipleAttributesPanel: FC<{ attributes: ComposerAttributeNode[] }> = ({ attributes }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [allAttributes, cloneAttributes, removeAttributes, updateAttribute, selectAttributes, selectOptions, materials, objects] = useComposerStore(
        (store) => [
            store.attributes,
            store.cloneAttributes,
            store.removeAttributes,
            store.updateAttribute,
            store.selectAttributes,
            store.selectOptions,
            store.sceneMaterialList,
            store.sceneObjectList
        ]
    );
    const selectedIDs = new Set(attributes.map((attribute) => attribute.id));
    const collectDescendants = (parents: ComposerAttributeNode[]) => {
        const descendants: ComposerAttributeNode[] = [];
        const visit = (parent: ComposerAttributeNode) => {
            allAttributes
                .filter((candidate) => candidate.parentID === parent.id)
                .forEach((child) => {
                    descendants.push(child);
                    visit(child);
                });
        };
        parents.forEach(visit);
        return descendants.filter((attribute, index, list) => list.findIndex((candidate) => candidate.id === attribute.id) === index);
    };
    const descendants = collectDescendants(attributes).filter((attribute) => !selectedIDs.has(attribute.id));
    const actionableAttributes = [...attributes, ...descendants].filter((attribute) => attribute.type === ComposerAttributeType.Attribute);
    const actions = actionableAttributes.flatMap((attribute) => attribute.options.flatMap((option) => option.actions));

    const duplicateSelection = () => {
        const cloned = cloneAttributes([...descendants, ...attributes]);
        const clonedSelection = cloned.slice(Math.max(0, cloned.length - attributes.length));
        selectAttributes(clonedSelection);
        selectOptions(null);
    };

    const setSelectionEnabled = (enabled: boolean) => {
        attributes.forEach((attribute) => updateAttribute({ ...attribute, enabled }));
    };

    const removeSelection = () => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected structure rows and their children?', 'Admin')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            removeAttributes([...descendants, ...attributes].map((attribute) => attribute.id));
                            selectAttributes(null);
                            selectOptions(null);
                        }
                    }
                ]}
            />
        );
    };

    const retargetSelection = () => {
        if (actions.length === 0) return;
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={materials}
                objectsList={objects}
                usedMaterialsList={materials.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={objects.filter((object) => objectIds.includes(object.id) || objectIds.includes(object.name || ''))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;
                    const remapped = retargetMapToActions(actions, data.materialMap, data.objectMap);
                    actionableAttributes.forEach((attribute) =>
                        updateAttribute({
                            ...attribute,
                            options: attribute.options.map((option) => ({
                                ...option,
                                actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                            }))
                        })
                    );
                }}
            />
        );
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='neutral'>{attributes.length}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectName>{T._('Structure selection', 'Admin')}</ComposerSubjectName>
                    <ComposerSubjectMeta>{T._(`${attributes.length} selected`, 'Admin')}</ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerScrollBody>
                <SelectionList>
                    {attributes.map((attribute) => (
                        <SelectionItem key={attribute.id}>{attribute.name}</SelectionItem>
                    ))}
                </SelectionList>
                <BulkButtons>
                    <BasicButton onClick={() => setSelectionEnabled(true)}>{T._(`Enable ${attributes.length} rows`, 'Admin')}</BasicButton>
                    <BasicButton onClick={() => setSelectionEnabled(false)}>{T._(`Disable ${attributes.length} rows`, 'Admin')}</BasicButton>
                    <BasicButton disabled={actions.length === 0} onClick={retargetSelection}>
                        {T._('Retarget selection…', 'Admin')}
                    </BasicButton>
                    <BasicButton onClick={duplicateSelection}>{T._(`Duplicate ${attributes.length} rows`, 'Admin')}</BasicButton>
                    <BasicButton onClick={removeSelection}>{T._(`Remove ${attributes.length} rows`, 'Admin')}</BasicButton>
                </BulkButtons>
            </ComposerScrollBody>
        </ComposerPanel>
    );
};

const MultipleOptionsPanel: FC<{ options: ComposerAttributeOptionNode[] }> = ({ options }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [cloneOption, removeOption, selectOptions, updateOption] = useComposerStore((store) => [
        store.cloneOption,
        store.removeOption,
        store.selectOptions,
        store.updateOption
    ]);

    const notConnected = (feature: string) =>
        showInfoNotification({ message: T._(`${feature} is shown by the prototype but is not connected to the current product API.`, 'Admin') });

    const cloneSelection = () => {
        const cloned = options.map(cloneOption);
        selectOptions(cloned);
    };

    const setSelectionEnabled = (enabled: boolean) => {
        options.forEach((option) => updateOption({ ...option, enabled }));
    };

    const removeSelection = () => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected options?', 'Admin')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            options.forEach((option) => removeOption(option.attributeID, option.id));
                            selectOptions([]);
                        }
                    }
                ]}
            />
        );
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='option'>{options.length}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectName>{T._('Options', 'Admin')}</ComposerSubjectName>
                    <ComposerSubjectMeta>{T._(`${options.length} selected`, 'Admin')}</ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerScrollBody>
                <SelectionList>
                    {options.map((option) => (
                        <SelectionItem key={option.id}>{option.name}</SelectionItem>
                    ))}
                </SelectionList>
                <BulkButtons>
                    <BasicButton onClick={() => setSelectionEnabled(true)}>{T._('Enable all options', 'Admin')}</BasicButton>
                    <BasicButton onClick={() => setSelectionEnabled(false)}>{T._('Disable all options', 'Admin')}</BasicButton>
                    <BasicButton onClick={() => notConnected(T._('Add the same action to all', 'Admin'))}>
                        {T._('Add the same action to all…', 'Admin')}
                    </BasicButton>
                    <BasicButton onClick={() => notConnected(T._('Set the same price', 'Admin'))}>{T._('Set the same price…', 'Admin')}</BasicButton>
                    <BasicButton onClick={cloneSelection}>{T._(`Clone ${options.length} options`, 'Admin')}</BasicButton>
                    <BasicButton onClick={removeSelection}>{T._(`Remove ${options.length} options`, 'Admin')}</BasicButton>
                </BulkButtons>
            </ComposerScrollBody>
        </ComposerPanel>
    );
};

const ResizeBoundary: FC<{
    label: string;
    onResize: (width: number) => void;
    onReset: () => void;
}> = ({ label, onResize, onReset }) => {
    const startResize = (event: React.MouseEvent<HTMLDivElement>) => {
        const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
        if (!previousPanel) return;
        const startX = event.clientX;
        const startWidth = previousPanel.getBoundingClientRect().width;
        const move = (moveEvent: MouseEvent) => onResize(Math.max(240, startWidth + moveEvent.clientX - startX));
        const stop = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    };

    return (
        <ComposerResizeHandle
            aria-label={label}
            aria-orientation='vertical'
            onDoubleClick={onReset}
            onKeyDown={(event) => {
                const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
                if (!previousPanel || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
                event.preventDefault();
                onResize(Math.max(240, previousPanel.getBoundingClientRect().width + (event.key === 'ArrowRight' ? 16 : -16)));
            }}
            onMouseDown={startResize}
            role='separator'
            tabIndex={0}
            title={T._('Drag to resize · double-click to reset', 'Admin')}
        />
    );
};

const ComposerAttributeContentPage: FC = () => {
    const [attributes, selectedAttributes, selectedOptions, storeMeshList, storeMaterialsList, storeCameraList] = useComposerStore((store) => [
        store.attributes,
        store.selectedAttributes,
        store.selectedOptions,
        store.sceneObjectList,
        store.sceneMaterialList,
        store.sceneCameraList
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const [optionsWidth, setOptionsWidth] = React.useState<number>();
    const [detailsWidth, setDetailsWidth] = React.useState<number>();
    const selectedAttribute = selectedAttributes?.[0] ?? null;
    const options = selectedAttribute?.options ?? EMPTY_OPTIONS;
    const actions = selectedOptions?.length === 1 ? selectedOptions[0].actions : EMPTY_ACTIONS;
    const multipleAttributes = (selectedAttributes?.length ?? 0) > 1;
    const multipleOptions = (selectedOptions?.length ?? 0) > 1;
    const createOptionOverlay = useCallback(() => () => null, []);
    const createActionOverlay = useCallback(() => () => null, []);
    const optionTreeState = useTreeNodeManager<ComposerAttributeOptionNode>(options, createOptionOverlay);
    const actionsTreeState = useTreeNodeManager<ComposerOptionActionNode>(actions, createActionOverlay);
    const meshes = storeMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).sort(ascComparator((mesh) => mesh.name ?? ''));
    const materials = storeMaterialsList
        .filter((material) => material.id !== 'default material' && material.name !== 'default material')
        .sort(ascComparator((material) => material.name ?? ''));
    const cameras = storeCameraList
        .filter((camera) => camera.id !== 'default camera' && camera.name !== 'default camera')
        .sort(ascComparator((camera) => camera.name ?? ''));

    const optionsPanel = multipleAttributes ? (
        <MultipleAttributesPanel attributes={selectedAttributes ?? []} />
    ) : selectedAttribute?.type === ComposerAttributeType.Attribute ? (
        <ComposerOptions selectedAttribute={selectedAttribute} options={options} multiple={false} state={optionTreeState} />
    ) : (
        <SubjectPanel subject={selectedAttribute ?? undefined} attributes={attributes} />
    );

    const detailsPanel =
        selectedAttribute?.type === ComposerAttributeType.Attribute && !multipleAttributes ? (
            multipleOptions ? (
                <MultipleOptionsPanel options={selectedOptions ?? []} />
            ) : selectedOptions?.length === 1 ? (
                <ComposerActions
                    selectedAttribute={selectedAttribute}
                    selectedOption={selectedOptions[0]}
                    actions={actions}
                    multiple={false}
                    state={actionsTreeState}
                    materials={materials}
                    meshes={meshes}
                    cameras={cameras}
                />
            ) : (
                <ComposerPanel>
                    <ComposerEmptyState>{T._('Pick an option in the list to see what it does.', 'Admin')}</ComposerEmptyState>
                </ComposerPanel>
            )
        ) : (
            <ComposerPanel>
                <ComposerEmptyState>{T._('Option details appear here when one attribute and one option are selected.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );

    return (
        <WorkspaceGrid $optionsWidth={optionsWidth} $detailsWidth={detailsWidth}>
            {optionsPanel}
            <ResizeBoundary label={T._('Resize options column', 'Admin')} onResize={setOptionsWidth} onReset={() => setOptionsWidth(undefined)} />
            {detailsPanel}
            <ResizeBoundary label={T._('Resize details column', 'Admin')} onResize={setDetailsWidth} onReset={() => setDetailsWidth(undefined)} />
            <ComposerPreview />
        </WorkspaceGrid>
    );
};

export default ComposerAttributeContentPage;
